import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAuth } from '../hooks/useAuth';
import {
  getCompiledDashboardWidgetSourceOptions,
  getCompiledAddonRouteExtensions,
  getCompiledHostSlotExtensions,
  getCompiledMainNavExtensions,
} from '../addons/compiledAddonRegistry';
import {
  AddonExtension,
  AddonExtensionPoint,
  DashboardWidgetSourceOption,
  EnabledAddon,
} from '../types/addons';
import { addonApi, VISUALISATION_ADDON_ID } from '../utils/addonApi';

export interface AddonsContextType {
  enabledAddons: EnabledAddon[];
  enabledExtensions: AddonExtension[];
  addonRouteExtensions: AddonExtension[];
  mainNavExtensions: AddonExtension[];
  workspaceHomeCardExtensions: AddonExtension[];
  dataObjectActionExtensions: AddonExtension[];
  adminSettingsPanelExtensions: AddonExtension[];
  auditEventViewerExtensions: AddonExtension[];
  dashboardWidgetExtensions: AddonExtension[];
  dashboardWidgetSourceOptions: DashboardWidgetSourceOption[];
  visualisationAddon: EnabledAddon | null;
  visualisationEnabled: boolean;
  loading: boolean;
  error: Error | null;
  getExtensionsByPoint: (extensionPoint: AddonExtensionPoint | string) => AddonExtension[];
  refreshAddons: () => Promise<void>;
}

const defaultContext: AddonsContextType = {
  enabledAddons: [],
  enabledExtensions: [],
  addonRouteExtensions: [],
  mainNavExtensions: [],
  workspaceHomeCardExtensions: [],
  dataObjectActionExtensions: [],
  adminSettingsPanelExtensions: [],
  auditEventViewerExtensions: [],
  dashboardWidgetExtensions: [],
  dashboardWidgetSourceOptions: [],
  visualisationAddon: null,
  visualisationEnabled: false,
  loading: true,
  error: null,
  getExtensionsByPoint: () => [],
  refreshAddons: async () => {},
};

export const AddonsContext = createContext<AddonsContextType>(defaultContext);

interface AddonsProviderProps {
  children: ReactNode;
}

export const AddonsProvider: React.FC<AddonsProviderProps> = ({ children }) => {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const [enabledAddons, setEnabledAddons] = useState<EnabledAddon[]>([]);
  const [enabledExtensions, setEnabledExtensions] = useState<AddonExtension[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshAddons = useCallback(async () => {
    setLoading(true);
    try {
      const [addons, extensions] = await Promise.all([
        addonApi.listEnabled(),
        addonApi.listExtensions(),
      ]);
      setEnabledAddons(Array.isArray(addons) ? addons : []);
      setEnabledExtensions(Array.isArray(extensions) ? extensions : []);
      setError(null);
    } catch (err) {
      console.warn('Failed to fetch addon discovery from backend; hiding addon extensions:', err);
      setEnabledAddons([]);
      setEnabledExtensions([]);
      setError(err instanceof Error ? err : new Error('Failed to fetch enabled addons'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!isAuthenticated) {
      setEnabledAddons([]);
      setEnabledExtensions([]);
      setError(null);
      setLoading(false);
      return;
    }

    refreshAddons();
  }, [authLoading, isAuthenticated, refreshAddons]);

  const visualisationAddon = useMemo(
    () => enabledAddons.find(addon => addon.id === VISUALISATION_ADDON_ID) || null,
    [enabledAddons]
  );

  const addonRouteExtensions = useMemo(
    () => getCompiledAddonRouteExtensions(enabledExtensions),
    [enabledExtensions]
  );

  const mainNavExtensions = useMemo(
    () => getCompiledMainNavExtensions(enabledExtensions),
    [enabledExtensions]
  );

  const workspaceHomeCardExtensions = useMemo(
    () => getCompiledHostSlotExtensions(enabledExtensions, 'workspace.home.card'),
    [enabledExtensions]
  );

  const dataObjectActionExtensions = useMemo(
    () => getCompiledHostSlotExtensions(enabledExtensions, 'dataObject.readAction'),
    [enabledExtensions]
  );

  const adminSettingsPanelExtensions = useMemo(
    () => getCompiledHostSlotExtensions(enabledExtensions, 'admin.settings.panel'),
    [enabledExtensions]
  );

  const auditEventViewerExtensions = useMemo(
    () => getCompiledHostSlotExtensions(enabledExtensions, 'audit.event.viewer'),
    [enabledExtensions]
  );

  const dashboardWidgetExtensions = useMemo(
    () => getCompiledHostSlotExtensions(enabledExtensions, 'dashboard.widget'),
    [enabledExtensions]
  );

  const dashboardWidgetSourceOptions = useMemo(
    () => getCompiledDashboardWidgetSourceOptions(dashboardWidgetExtensions),
    [dashboardWidgetExtensions]
  );

  const getExtensionsByPoint = useCallback(
    (extensionPoint: AddonExtensionPoint | string) => {
      switch (extensionPoint) {
        case 'addon.route':
          return addonRouteExtensions;
        case 'main.nav':
          return mainNavExtensions;
        case 'workspace.home.card':
          return workspaceHomeCardExtensions;
        case 'dataObject.readAction':
          return dataObjectActionExtensions;
        case 'admin.settings.panel':
          return adminSettingsPanelExtensions;
        case 'audit.event.viewer':
          return auditEventViewerExtensions;
        case 'dashboard.widget':
          return dashboardWidgetExtensions;
        default:
          return [];
      }
    },
    [
      addonRouteExtensions,
      adminSettingsPanelExtensions,
      auditEventViewerExtensions,
      dashboardWidgetExtensions,
      dataObjectActionExtensions,
      mainNavExtensions,
      workspaceHomeCardExtensions,
    ]
  );

  const value = useMemo(
    () => ({
      enabledAddons,
      enabledExtensions,
      addonRouteExtensions,
      mainNavExtensions,
      workspaceHomeCardExtensions,
      dataObjectActionExtensions,
      adminSettingsPanelExtensions,
      auditEventViewerExtensions,
      dashboardWidgetExtensions,
      dashboardWidgetSourceOptions,
      visualisationAddon,
      visualisationEnabled: Boolean(visualisationAddon),
      loading,
      error,
      getExtensionsByPoint,
      refreshAddons,
    }),
    [
      addonRouteExtensions,
      adminSettingsPanelExtensions,
      auditEventViewerExtensions,
      dataObjectActionExtensions,
      dashboardWidgetExtensions,
      dashboardWidgetSourceOptions,
      enabledAddons,
      enabledExtensions,
      error,
      getExtensionsByPoint,
      loading,
      mainNavExtensions,
      refreshAddons,
      visualisationAddon,
      workspaceHomeCardExtensions,
    ]
  );

  return <AddonsContext.Provider value={value}>{children}</AddonsContext.Provider>;
};
